import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { Modal } from "flowbite-react";
import AuthProvider from "../services/auth/AuthProvider/Index";
import AuthStateStorage from "../services/auth/AuthProvider/AuthStateStorage";
import CenteredLoader from "../components/CenteredLoader";
import DefaultLayout from "../layouts/default";
import { setUserData } from "../../redux/actions/userDataActions";
import { Navigation, UserNavigation } from "../types";
import ProfileUpdate from "../pages/ProfileUpdate";
import { Button } from "./Form/Button";
import notifications from "../services/notifications";

const AUTO_LOGOUT_STORAGE_KEY = `${process.env.REACT_APP_STORAGE_NAMESPACE}_auto_logout`;

const events = [
  "load",
  "mousemove",
  "mousedown",
  "click",
  "scroll",
  "keypress",
];

type AutoLogoutAlertProps = {
  countdownDuration?: number;
  onStayConnected: () => void;
};

const AutoLogoutAlert: React.FC<AutoLogoutAlertProps> = ({
  countdownDuration = Number.parseInt(`${process.env.REACT_APP_AUTOLOGOUT_COUNTDOWN}`, 10),
  onStayConnected,
}) => {
  const [countdown, setCountdown] = useState<number>(countdownDuration);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (countdown > 0) {
      timerRef.current = setInterval(() => {
        setCountdown(countdown - 1);
      }, 1000);

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
    } else {
      (async () => {
        await AuthProvider.logout();
      })();
    }
  }, [countdown, onStayConnected]);

  return (
    <Modal show={true}>
      <Modal.Header>Inactivity Timeout Alert</Modal.Header>
      <Modal.Body>
        <div className="w-full mt-6">
          <p>
            Your session is about to expire due to inactivity in...{" "}
            <b>{countdown} seconds</b>.
          </p>
          <br />
          <p>
            If you'd like to continue with your session, click the "Stay
            connected" button below.
          </p>
          <br />
          <p>
            Thank you for choosing our services, and for helping us maintain a
            secure online environment.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="w-full flex flex-col gap-3 md:flex-row md:justify-end">
          <Button
            label="Stay connected"
            theme="primary"
            size="medium"
            className="w-full md:w-48 whitespace-nowrap"
            onClick={() => {
              if (timerRef.current) {
                clearInterval(timerRef.current);
              }

              localStorage.setItem(AUTO_LOGOUT_STORAGE_KEY, String(Date.now()));

              onStayConnected();
            }}
          />
          <Button
            label="Log Out"
            theme="secondary"
            size="medium"
            className="w-full md:w-48 whitespace-nowrap"
            onClick={async () => {
              if (timerRef.current) {
                clearInterval(timerRef.current);
              }

              await AuthProvider.logout();
            }}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const AuthenticatedRoute = ({
  component: Component,
  title = "",
  navigation,
  userNavigation,
  profileSetupPage: ProfileSetupPage = ProfileUpdate,
}: {
  component: any;
  title: string;
  navigation?: Navigation;
  userNavigation?: UserNavigation;
  profileSetupPage?: any;
}) => {
  const [loading, setLoading] = useState(true);
  const [loggedIn, setLoggedIn] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [showAutoLogoutAlert, setShowAutoLogoutAlert] = useState(false);
  const dispatch = useDispatch();
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const dispatchSetUserData = (value: any) => {
      dispatch(setUserData(value));
    };

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const handleAutoLogoutTimeout = async () => {
      clearTimer();
      removeActivityListeners();

      const minimumSessionLifespan = await AuthProvider.getMinimumSessionLifespan();
      const minSessionThreshold = Number.parseInt(
        `${process.env.REACT_APP_AUTOLOGOUT_MIN_SECS_SESSION_LIFESPAN}`,
        10
      );

      if (minimumSessionLifespan > minSessionThreshold) {
        setShowAutoLogoutAlert(true);
      }
    };

    const startAutoLogoutTimer = () => {
      clearTimer();
      timerRef.current = setTimeout(
        handleAutoLogoutTimeout,
        Number.parseInt(`${process.env.REACT_APP_AUTOLOGOUT_IN_MS}`, 10)
      );
    };

    const handleUserActivity = () => {
      clearTimer();
      startAutoLogoutTimer();
    };

    const activityHandlers = events.map((name) => ({
      name,
      handler: handleUserActivity,
    }));

    const addActivityListeners = () => {
      for (const { name, handler } of activityHandlers) {
        globalThis.addEventListener(name, handler);
      }
    };

    const removeActivityListeners = () => {
      for (const { name, handler } of activityHandlers) {
        globalThis.removeEventListener(name, handler);
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === AUTO_LOGOUT_STORAGE_KEY) {
        clearTimer();
        startAutoLogoutTimer();
        setShowAutoLogoutAlert(false);
      }
    };

    const initializeIdentity = async () => {
      try {
        const identity = await AuthProvider.getIdentity();
        if (!identity) {
          // getIdentity() should have redirected to login, but set loading to false
          // to allow the redirect to proceed
          setLoading(false);
          return;
        }

        dispatchSetUserData(identity);
        setLoggedIn(true);
        setInitialized(true);
        addActivityListeners();
        globalThis.addEventListener('storage', handleStorage);
        startAutoLogoutTimer();
        setLoading(false);
      } catch (error) {
        // If there's an error, set loading to false to allow redirect
        console.error('Error initializing identity:', error);
        setLoading(false);
      }
    };

    initializeIdentity();

    return () => {
      clearTimer();
      removeActivityListeners();
      globalThis.removeEventListener('storage', handleStorage);
    };
  }, [dispatch]);

  useEffect(() => {
    if (loggedIn && initialized) {
      const suffix = title ? `| ${title}` : "";
      document.title = `JackAtHome ${suffix}`;
    }

    if (loggedIn && !initialized) {
      const suffix = title ? "| Account setup" : "";
      document.title = `JackAtHome ${suffix}`;

      notifications.warning({ message: "Your profile information is not complete or is unverified, please complete and/or complete your profile information." });
    }
  }, [loggedIn, initialized, title]);

  useEffect(() => {
    if (loggedIn && !loading) {
      notifications.onReload();
    }
  }, [loggedIn, loading]);

  // Safety mechanism: explicitly redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !loggedIn) {
      // Small delay to allow getIdentity() redirect to happen first
      const redirectTimer = setTimeout(() => {
        const loginUrl = process.env.REACT_APP_USER_POOL_LOGIN_URL;
        if (loginUrl) {
          // Save current state before redirecting
          AuthStateStorage.save();
          globalThis.location.href = loginUrl;
        }
      }, 100);

      return () => clearTimeout(redirectTimer);
    }
  }, [loading, loggedIn]);

  const handleStayConnected = () => {
    setShowAutoLogoutAlert(false);
  };

  if (!loading && loggedIn && initialized) {
    return (
      <DefaultLayout navigation={navigation} userNavigation={userNavigation}>
        <>
          {showAutoLogoutAlert && (
            <AutoLogoutAlert onStayConnected={handleStayConnected} />
          )}
          <Component />
        </>
      </DefaultLayout>
    );
  }

  if (!loading && loggedIn && !initialized) {
    return <ProfileSetupPage isAccountSetup={true} />;
  }

  return <CenteredLoader />;
};

export default AuthenticatedRoute;